import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import StyledButton from "components/shared/StyledButton";
import type { FieldData } from "formFields";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ICustomField } from "store/customFieldsStore";
import { updateCustomField } from "store/customFieldsStore";
import { useAppDispatch } from "store/store";

import { flagToBoolean } from "../customFieldPayload";

import styles from "./style.module.scss";

interface IChangeSectionModal {
    /**
     * All existing sections within form.
     */
    sections: string[];
    /**
     * All fields within form.
     */
    fields: Record<string, FieldData>;
    /**
     * Field that is being updated.
     */
    chosenField: ICustomField | null;
    /**
     * @param data Custom field being updated.
     * @returns Triggers update of allFields in parent component.
     */
    onUpdate: (data: Omit<ICustomField, "id" | "companyId" | "uuid" | "isDeleted">) => void;
}

// Modal to change the section to which a custom field belongs.
const ChangeSectionModal = ({ fields, sections, chosenField, onUpdate }: IChangeSectionModal) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const defaultValues = {
        formLocation: "",
    };

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm({ defaultValues });

    const onSubmit = (data: { formLocation: string }) => {
        if (chosenField && data.formLocation !== "") {
            dispatch(
                updateCustomField({
                    fieldId: chosenField.id,
                    data: {
                        name: chosenField.name,
                        helperText: chosenField.helperText,
                        valueType: chosenField.valueType,
                        formLocation: data.formLocation,
                        entityType: chosenField.entityType,
                        isRequired: flagToBoolean(chosenField.isRequired),
                        showOnCreation: flagToBoolean(chosenField.showOnCreation),
                        showOnPortal: flagToBoolean(chosenField.showOnPortal),
                        includeInSalesExport: flagToBoolean(chosenField.includeInSalesExport),
                    },
                }),
            ).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    onUpdate && onUpdate({ ...chosenField, formLocation: data.formLocation });
                }
            });
        }
    };

    return (
        <form className={styles["move-field-container"]} onSubmit={handleSubmit(onSubmit)}>
            <AutocompleteFormComponent
                options={sections}
                renderOptionText={(option) => fields[option]?.label ?? ""}
                placeholder={t("CustomFields.ChangeSection.ChooseSection")}
                label={t("CustomFields.ChangeSection.FieldLabel")}
                control={control}
                controlName="formLocation"
                required
                error={errors.formLocation}
            />
            <StyledButton fullWidth buttonType="primary" className={styles["submit-btn"]} type="submit">
                {t("CustomFields.ChangeSection.Confirm")}
            </StyledButton>
        </form>
    );
};

export default ChangeSectionModal;
